import type { CSSProperties } from 'react';
import type { TreemapNodeData } from './treemap-types';
import { TreemapNodeKind } from './treemap-types';
import {
  DEPTH_COLORS,
  DIMMED_OPACITY,
  HEADER_HEIGHT,
  LABEL_MIN_HEIGHT,
  LABEL_MIN_WIDTH,
  NODE_TEXT,
  SECTION_BODY_FILL,
  TRANSITION_DURATION,
  VALUE_MIN_HEIGHT,
} from './treemap-constants';
import { formatTreemapValue, hexToRgba } from './treemap-format';
import { getFontFamily } from '@/theme/typography';

const SECTION_BORDER_RADIUS = 6;
const LEAF_BORDER_RADIUS = 4;
const SECTION_BORDER_ALPHA = 0.5;
const SECTION_HEADER_FILL_ALPHA = 0.12;
const SECTION_HEADER_BORDER_ALPHA = 0.18;
const LEAF_BORDER_ALPHA = 0.55;
const LEAF_FILL_ALPHA = 0.22;
const TREEMAP_CELL_TRANSITION = `opacity ${TRANSITION_DURATION} ease-out, border-color ${TRANSITION_DURATION} ease-out`;

export const TREEMAP_LABEL_TEXT_ALIGN_CENTER = 'center';

export const TREEMAP_LABEL_TEXT_STYLE: CSSProperties = {
  lineHeight: '16px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const TREEMAP_LABEL_WRAPPER_STYLE: CSSProperties = {
  minWidth: 0,
  maxWidth: '100%',
  pointerEvents: 'none',
  userSelect: 'none',
};

export const TREEMAP_VALUE_BASE_STYLE: CSSProperties = {
  fontFamily: getFontFamily(),
  fontSize: 11,
  opacity: 0.7,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  userSelect: 'none',
};

interface GetTreemapCellNodeModelInput {
  data: TreemapNodeData;
  width: number | null | undefined;
  height: number | null | undefined;
  hoveredNodeId: string | null;
  activeNodeIds: ReadonlySet<string>;
}

interface TreemapCellCommonModel {
  label: string;
  formattedValue: string;
  showLabel: boolean;
  showValue: boolean;
  valueColor: string;
  labelColor: string;
}

interface TreemapCellClassModel {
  fill: string | null;
  stroke: string | null;
  text: string | null;
}

interface TreemapCellBaseModel extends TreemapCellCommonModel {
  color: string;
  classStyle: TreemapCellClassModel;
  isHovered: boolean;
  rootOpacity: number;
  transition: string;
}

interface TreemapCellVisibility {
  showLabel: boolean;
  showValue: boolean;
}

const EMPTY_TREEMAP_CELL_CLASS_MODEL: TreemapCellClassModel = {
  fill: null,
  stroke: null,
  text: null,
};

export interface TreemapSectionCellModel extends TreemapCellCommonModel {
  kind: TreemapNodeKind.Section;
  rootStyle: CSSProperties;
  headerStyle: CSSProperties;
}

export interface TreemapLeafCellModel extends TreemapCellCommonModel {
  kind: TreemapNodeKind.Leaf;
  rootStyle: CSSProperties;
}

export type TreemapCellNodeModel = TreemapSectionCellModel | TreemapLeafCellModel;

export function getTreemapCellNodeModel(input: GetTreemapCellNodeModelInput): TreemapCellNodeModel {
  const baseModel = getTreemapCellBaseModel(input);

  return input.data.kind === TreemapNodeKind.Section
    ? getTreemapSectionCellModel(baseModel)
    : getTreemapLeafCellModel(baseModel);
}

function getTreemapCellBaseModel({
  data,
  width,
  height,
  hoveredNodeId,
  activeNodeIds,
}: GetTreemapCellNodeModelInput): TreemapCellBaseModel {
  const color = getTreemapDepthColor(data.colorIndex);
  const classStyle = getTreemapCellClassModel(data);
  const visibility = getTreemapCellVisibility({ data, width, height });

  return {
    label: data.label,
    formattedValue: formatTreemapValue(data.value),
    showLabel: visibility.showLabel,
    showValue: visibility.showValue,
    valueColor: classStyle.text ?? NODE_TEXT,
    labelColor: classStyle.text ?? getDefaultLabelColor({ kind: data.kind, color }),
    color,
    classStyle,
    isHovered: hoveredNodeId === data.nodeId,
    rootOpacity: getTreemapCellOpacity({ hoveredNodeId, activeNodeIds, nodeId: data.nodeId }),
    transition: TREEMAP_CELL_TRANSITION,
  };
}

function getTreemapCellVisibility(input: {
  data: TreemapNodeData;
  width: number | null | undefined;
  height: number | null | undefined;
}): TreemapCellVisibility {
  const cellWidth = input.width ?? 0;
  const cellHeight = input.height ?? 0;

  return {
    showLabel: cellWidth >= LABEL_MIN_WIDTH && cellHeight >= LABEL_MIN_HEIGHT,
    showValue: input.data.showValue && cellHeight >= VALUE_MIN_HEIGHT,
  };
}

function getTreemapCellClassModel(data: TreemapNodeData): TreemapCellClassModel {
  const classStyle = data.classStyle;
  if (!classStyle) {
    return EMPTY_TREEMAP_CELL_CLASS_MODEL;
  }

  return {
    fill: getOptionalClassValue(classStyle.fill),
    stroke: getOptionalClassValue(classStyle.stroke),
    text: getOptionalClassValue(classStyle.color),
  };
}

function getOptionalClassValue(value: string | undefined): string | null {
  return value ?? null;
}

function getTreemapCellOpacity(input: {
  hoveredNodeId: string | null;
  activeNodeIds: ReadonlySet<string>;
  nodeId: string;
}): number {
  if (!input.hoveredNodeId) {
    return 1;
  }

  return input.activeNodeIds.has(input.nodeId) ? 1 : DIMMED_OPACITY;
}

function getTreemapSectionCellModel(baseModel: TreemapCellBaseModel): TreemapSectionCellModel {
  const { color, classStyle, isHovered, rootOpacity, transition, ...commonModel } = baseModel;

  return {
    ...commonModel,
    kind: TreemapNodeKind.Section,
    rootStyle: getSectionRootStyle({
      color,
      classFill: classStyle.fill,
      classStroke: classStyle.stroke,
      isHovered,
      opacity: rootOpacity,
      transition,
    }),
    headerStyle: getSectionHeaderStyle(color),
  };
}

function getTreemapLeafCellModel(baseModel: TreemapCellBaseModel): TreemapLeafCellModel {
  const { color, classStyle, isHovered, rootOpacity, transition, ...commonModel } = baseModel;

  return {
    ...commonModel,
    kind: TreemapNodeKind.Leaf,
    rootStyle: getLeafRootStyle({
      color,
      classFill: classStyle.fill,
      classStroke: classStyle.stroke,
      isHovered,
      opacity: rootOpacity,
      transition,
    }),
  };
}

function getTreemapDepthColor(colorIndex: number): string {
  return DEPTH_COLORS[colorIndex % DEPTH_COLORS.length] ?? DEPTH_COLORS[0];
}

function getDefaultLabelColor(input: {
  kind: TreemapNodeKind;
  color: string;
}): string {
  return input.kind === TreemapNodeKind.Section ? input.color : NODE_TEXT;
}

function getSectionRootStyle(input: {
  color: string;
  classFill: string | null;
  classStroke: string | null;
  isHovered: boolean;
  opacity: number;
  transition: string;
}): CSSProperties {
  const borderColor = input.isHovered
    ? input.color
    : input.classStroke ?? hexToRgba(input.color, SECTION_BORDER_ALPHA);

  return {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: SECTION_BORDER_RADIUS,
    border: `1px solid ${borderColor}`,
    backgroundColor: input.classFill ?? SECTION_BODY_FILL,
    opacity: input.opacity,
    transition: input.transition,
    cursor: 'pointer',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };
}

function getSectionHeaderStyle(color: string): CSSProperties {
  return {
    height: HEADER_HEIGHT,
    minHeight: HEADER_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '0 8px',
    backgroundColor: hexToRgba(color, SECTION_HEADER_FILL_ALPHA),
    borderBottom: `1px solid ${hexToRgba(color, SECTION_HEADER_BORDER_ALPHA)}`,
  };
}

function getLeafRootStyle(input: {
  color: string;
  classFill: string | null;
  classStroke: string | null;
  isHovered: boolean;
  opacity: number;
  transition: string;
}): CSSProperties {
  const borderColor = input.isHovered
    ? input.classStroke ?? input.color
    : input.classStroke ?? hexToRgba(input.color, LEAF_BORDER_ALPHA);

  return {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: LEAF_BORDER_RADIUS,
    border: `1px solid ${borderColor}`,
    backgroundColor: input.classFill ?? hexToRgba(input.color, LEAF_FILL_ALPHA),
    opacity: input.opacity,
    transition: input.transition,
    cursor: 'default',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    padding: '4px 6px',
    textAlign: 'center',
  };
}
